import { useCallback, useEffect, useMemo, useState } from "react";
import "./SelectOrganization.css";
import { useLocation, useNavigate } from "react-router-dom";
import UserService from "../../services/UserService";
import Loader from "../../components/Loader/Loader";
import { useAuth } from "../../contexts/auth";
import { useUserStore } from "../../contexts/userStore";
import { useIndexedDb } from "../../contexts/indexedDb";
import { MostAccessBadge } from "../../components/MostAccessBadge/MostAccessBadge";
import SearchInput from "../../components/SearchInput/SearchInput";
import { debounce } from "../../utils/timer";
import Pagination from "../../components/Pagination/Pagination";
import { CountdownTimer } from "../../components/CountdownTimer/CountdownTimer";

let controller = new AbortController();

function SelectOrganization() {
	const [showLoader, setShowLoader] = useState(false);
	const [searchLoading, setSearchLoading] = useState(false);
	const { user, storeUser, storeToken, accessToken } = useAuth();
	const { getUrlPreference, clear } = useUserStore();
	const navigate = useNavigate();
	const location = useLocation();
	const [mustRedirect, setMustRedirect] = useState(false);
	const { saveImage, remove } = useIndexedDb();
	const hasRootAccess = (user.userData.isSuperAdmin || user.userData.hasRootAccess);
	const [mostAccess, setMostAccess] = useState(null);
	const [totalAccessible, setTotalAccessible] = useState(0);
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [lastOrganizations, setLastOrganizations] = useState([]);
	const [organizations, setOrganizations] = useState([]);
	const debounceSearch = useCallback(
		debounce(fetchOrganizations, 650), []
	);

	async function fetchOrganizations(search, page, controller) {
		setSearchLoading(true);
		const orgs = await UserService.getAccessibleOrganizations(search, page, 12, controller);
		setSearchLoading(false);
		if (orgs) {
			setTotalPages(orgs.data.pagination.totalPages);
			setOrganizations(orgs.data.organizations);
		}
	}

	useEffect(() => {
		setPage(1)
		setTotalPages(1)
		if (search === "") {
			setOrganizations(null)
			controller.abort();
			return;
		}
		controller = new AbortController();
		debounceSearch(search, 1, controller);
	}, [search])

	useEffect(() => {
		async function loadOrganizations() {
			setShowLoader(true);
			const { data: payload } = await UserService.getLastAccessibleOrganizations();
			setShowLoader(false);
			setLastOrganizations(payload.organizations.items);
			if (payload.organizations?.items?.length === 1 && !hasRootAccess) {
				handleSelect(payload.organizations.items[0].id);
				return;
			}
			if (hasRootAccess && payload.organizations?.items?.length === 0) {
				handleSelect(null);
			}
			setTotalAccessible(payload.organizations.total);
			let tempMostAccess = {
				entityId: null,
				total: 0,
			};
			for (const org of payload.organizations.items) {
				if (org?.lastAccessData?.total > tempMostAccess.total) {
					tempMostAccess = {
						entityId: org.id,
						total: org.lastAccessData.total,
					};
				}
			}
			if (tempMostAccess.entityId) {
				setMostAccess(tempMostAccess.entityId);
			}
		}
		loadOrganizations();
	}, [])

	async function handleSelect(organizationId) {
		if (user.userData?.selectedOrganizationId === organizationId) {
			setMustRedirect(true);
			return;
		}
		clear();
		setShowLoader(true);
		const response = await UserService.selectOrganization(organizationId);
		setShowLoader(false);
		storeToken(response.data?.accessToken ?? accessToken)
		if (!response) {
			return;
		}
		setShowLoader(true);
		const responseUser = await UserService.getUserData();
		setShowLoader(false);
		storeUser({
			userData: {
				...responseUser.data,
				selectedUnitId: null,
				selectedUnit: null,
				selectedOrganizationId: organizationId,
			}
		});
		if (responseUser.data.selectedOrganization) {
			saveImage(`organization-logo`, responseUser.data.selectedOrganization.urlLogo);
		} else {
			remove(`organization-logo`);
		}
		setMustRedirect(true);
	}

	useEffect(() => {
		if (user.userData && mustRedirect) {
			if (user.userData.selectOrganizationId && location.state?.ref) {
				navigate(location.state?.ref);
			} else {
				navigate(getUrlPreference());
			}
			setMustRedirect(false);
		}
	}, [user, mustRedirect]);

	return (
		<div className="select-organization-container paper-background">
			<h1>Seja Bem Vindo {user.userData.name}!<br /> Selecione a empresa:</h1>

			{totalAccessible > 1 && (
				<div style={{ marginBottom: "20px" }}>
					<SearchInput
						isLoading={searchLoading}
						value={search ?? ""}
						placeholder="Buscar"
						onChange={({ target }) => setSearch(target.value)}
						onKeyPress={(e) => e.key === "Enter" && {}}
					/>
				</div>
			)}

			<div className="organizations-container">
				{showLoader ? (
					<div className="loader">
						<Loader
							styles={{
								marginTop: "0",
							}}
							width={25}
							height={25}
						/>
					</div>
				) : (
					<>
						{(organizations ?? lastOrganizations)?.map((org, idx) => (
							<button disabled={!org.nextShiftData?.fullTime && org.nextShiftData?.time > 0} key={idx} className="organization-button" onClick={(e) => handleSelect(org.id)}>
								{!org.nextShiftData?.fullTime && org.nextShiftData?.time > 0 && (
									<CountdownTimer date={org.nextShiftData?.nextDate} onEnd={() => {
										function prevState(orgs) {
											return orgs.map(_org => {
												if (_org.id === org.id) {
													return {
														..._org,
														nextShiftData: {
															..._org.nextShiftData,
															time: 0,
														}
													}
												}
												return _org;
											});
										}

										if (organizations) {
											setOrganizations(prevState);
										} else {
											setLastOrganizations(prevState);
										}
									}} />
								)}
								{org.name}
								{org.lastAccessData?.total && mostAccess === org.id && <MostAccessBadge />}
								{/* <BsFillPinAngleFill /> */}
							</button>
						))}
						{hasRootAccess && (
							<button className="organization-button" onClick={() => handleSelect(null)}>
								Bravas Root
							</button>
						)}
					</>
				)}
			</div>
			{totalPages > 1 && (
				<div style={{ marginTop: '20px' }}>
					<Pagination
						rowsPerPage={12}
						maxPage={totalPages}
						changePage={(page) => {
							setPage(page);
							fetchOrganizations(search, page, controller);
						}}
					/>
				</div>
			)}
		</div>
	);
}

export default SelectOrganization;
